<template>
  <div class="panel-root stack tight">
    <UiSectionHeader>
      <template #title>{{ ICONS.domains.work }} Resources</template>
      <template #actions>
        <UiChip v-if="isGathering" variant="active">Active</UiChip>
        <UiChip v-else variant="muted" class="stat-mono num">{{ formatNumber(resourceNodes.length) }}</UiChip>
        <UiButton v-if="isGathering" variant="danger" size="sm" :disabled="disabled" @click="emit('stop')">
          Stop
        </UiButton>
      </template>
    </UiSectionHeader>

    <UiCallout v-if="localError" variant="danger">{{ localError }}</UiCallout>

    <UiCallout v-if="isGathering && gatheringAction" variant="info">
      <div class="row wrap" style="justify-content: space-between;">
        <div>
          <strong>Gathering</strong>: {{ gatheringNodeName }}
          <div class="text-muted" style="margin-top: 0.25rem;">
            Skill {{ skillLabel(gatheringAction.skillId) }} • Interval
            <span class="stat-mono num">{{ formatNumber(Math.round(gatheringAction.intervalMs / 1000)) }}s</span>
          </div>
        </div>
        <UiChip v-if="nodeDepleted" variant="warning">{{ ICONS.states.warning }} Depleted</UiChip>
      </div>
    </UiCallout>

    <UiCallout v-else-if="resourceNodes.length === 0" variant="info">No resources here.</UiCallout>

    <div v-else class="list panel-scroll scroll">
      <ResourceNodeRow
        v-for="node in sortedNodes"
        :key="node.id"
        :node="node"
        :effectiveLevel="effectiveSkillLevel(node.skillId)"
        :modifier="skillDelta(node.skillId)"
        :canGather="canGatherNode(node)"
        :blockedReason="blockedContextForNode(node)?.message ?? null"
        :blockedReasonKind="blockedContextForNode(node)?.kind ?? null"
        :disabled="disabled"
        @gather="handleGather"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { EquipmentSlot, GatheringAction, InventoryEntry, ResourceNode, SkillId, SkillProgress } from "@idle-rpg/shared";
import { ICONS } from "../../lib/icons";
import { formatNumber } from "../../lib/format";
import { skillLabel } from "../../lib/skillMeta";
import ResourceNodeRow from "../world/rows/ResourceNodeRow.vue";
import UiButton from "../ui/UiButton.vue";
import UiCallout from "../ui/UiCallout.vue";
import UiChip from "../ui/UiChip.vue";
import UiSectionHeader from "../ui/UiSectionHeader.vue";

const props = defineProps<{
  resourceNodes: ResourceNode[];
  isGathering: boolean;
  gatheringAction: GatheringAction | null;
  nodeDepleted: boolean;
  skillProgress: SkillProgress[];
  effectSums: { skill: Partial<Record<SkillId, number>> } | null;
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
  inventory: InventoryEntry[];
  disabled: boolean;
}>();

const emit = defineEmits<{
  gather: [nodeId: string];
  stop: [];
}>();

const localError = ref<string>("");

const gatheringNodeName = computed(() => {
  const nodeId = props.gatheringAction?.nodeId;
  if (!nodeId) return "Unknown";
  return props.resourceNodes.find((n) => n.id === nodeId)?.name ?? "Unknown";
});

const sortedNodes = computed(() => {
  const nodes = [...props.resourceNodes];
  return nodes.sort((a, b) => {
    const aUnlocked = canGatherNode(a);
    const bUnlocked = canGatherNode(b);
    if (aUnlocked !== bUnlocked) return aUnlocked ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
});

function getSkillLevel(skillId: string): number {
  const skill = props.skillProgress.find((s) => s.id === skillId);
  return skill?.level ?? 1;
}

function skillDelta(skillId: string): number {
  const delta = (props.effectSums?.skill as any)?.[skillId];
  const v = Math.floor(Number(delta ?? 0));
  return Number.isFinite(v) ? v : 0;
}

function effectiveSkillLevel(skillId: string): number {
  const base = Math.max(1, Math.floor(Number(getSkillLevel(skillId) ?? 1)));
  const eff = base + skillDelta(skillId);
  return Math.max(0, Math.min(99, eff));
}

function canGatherNode(node: ResourceNode): boolean {
  if (effectiveSkillLevel(node.skillId) < node.levelReq) return false;
  if (node.requiredItemMode === "equipped" && node.requiredItemId) {
    return hasEquippedItem(node.requiredItemId);
  }
  if (node.requiredItemMode === "inventory" && node.requiredItemId) {
    return hasInventoryItem(node.requiredItemId);
  }
  return true;
}

function hasEquippedItem(itemId: string): boolean {
  for (const slot of Object.keys(props.equipment) as EquipmentSlot[]) {
    const entry = props.equipment[slot];
    if (entry?.itemId === itemId) return true;
  }
  return false;
}

function hasInventoryItem(itemId: string): boolean {
  return (props.inventory ?? []).some((entry) => entry.itemId === itemId && entry.quantity > 0);
}

type BlockedContext = { message: string; kind: "busy" | "level" | "item" };

function blockedContextForNode(node: ResourceNode): BlockedContext | null {
  if (props.disabled) return { message: "Busy", kind: "busy" };
  if (effectiveSkillLevel(node.skillId) < node.levelReq) {
    return { message: `Requires level ${node.levelReq}.`, kind: "level" };
  }
  if (node.requiredItemMode === "equipped" && node.requiredItemId && !hasEquippedItem(node.requiredItemId)) {
    return { message: `Requires ${node.requiredItemName || "a tool"} equipped.`, kind: "item" };
  }
  if (node.requiredItemMode === "inventory" && node.requiredItemId && !hasInventoryItem(node.requiredItemId)) {
    return { message: `Requires ${node.requiredItemName || "bait"} in inventory.`, kind: "item" };
  }
  return null;
}

function handleGather(nodeId: string) {
  localError.value = "";
  if (props.disabled) return;
  const node = props.resourceNodes.find((n) => n.id === nodeId);
  if (!node) return;
  const context = blockedContextForNode(node);
  if (context) {
    localError.value = context.message;
    return;
  }
  emit("gather", nodeId);
}
</script>

<style scoped>
.panel-root {
  min-width: 0;
}

.list {
  display: grid;
  gap: 0.6rem;
}
</style>
